import { DictType } from "@pma/types"
import { SERVER_URL, TASK_BY_CURRENT_USER_ENDPOINT } from "@pma/utils/api-routes"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { useQuery } from "@tanstack/react-query"
import axios from "axios"
import { parseCookies } from "nookies"
import Autoplay from "embla-carousel-autoplay"
import { Card, CardContent, CardHeader, CardTitle } from "@components/common/card"
import useTranslator from "@hooks/use-translator"
import { useAuth } from "@stores/auth-store"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@components/common/carousel"
import TaskCard from "../projects/task-card"
import { File, Pin } from "lucide-react"
import PmsLoader from "@components/common/loader"
import { useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { cn } from "@pma/utils/helpers"
import { Alert, AlertDescription, AlertTitle } from "@components/common/alert"

const PinnedTasks = () => {
  const { translate, locale } = useTranslator()

  const { authConfig } = useAuth()
  const [searchParam] = useSearchParams()

  const queryKey = searchParam.get("revalidate")
  const orgId = authConfig?.last_organization_id

  const accessToken = parseCookies()[ACCESS_TOKEN]

  const isHebrew = locale === "he"

  const getPinnedTasks = async () => {
    const response = await axios.post(
      SERVER_URL + TASK_BY_CURRENT_USER_ENDPOINT,
      {
        show_created_by: true,
        organization_id: orgId,
        paging_per_page: 25,
        is_pinned: true,
        show_deleted: false,
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const {
    data: pinnedTasks,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["get-pinned-tasks", orgId],
    queryFn: getPinnedTasks,
  })

  const sortedTasks =
    pinnedTasks?.items?.sort((a: DictType, b: DictType) => a.created_at - b.created_at) || []

  useEffect(() => {
    if (queryKey === "tasks") refetch()
  }, [queryKey])

  return (
    <Card className='my-6 space-y-3 overflow-hidden'>
      <CardHeader className='pb-0'>
        <CardTitle className='relative flex items-center gap-1 text-xl'>
          <Pin
            size={14}
            className={cn(
              "absolute -top-2",
              isHebrew ? "-right-4 rotate-45" : "-left-4 -rotate-45",
            )}
          />
          {translate("Pinned Tasks")}
        </CardTitle>
      </CardHeader>
      {isLoading ? (
        <PmsLoader />
      ) : sortedTasks.length > 0 ? (
        <Carousel
          opts={{
            align: "start",
            loop: true,
            direction: isHebrew ? "rtl" : "ltr",
          }}
          plugins={[Autoplay({ delay: 3000 })]}
          className='w-full p-4'
        >
          <CarouselContent className={cn("w-full", isHebrew ? "pr-4" : "pl-4")}>
            {sortedTasks?.map((task: DictType) => (
              <CarouselItem
                className='max-h-[270px] w-fit border border-none p-1 md:basis-1/2 lg:basis-1/3'
                key={task?.id}
              >
                <TaskCard task={task} />
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious />
          <CarouselNext />
        </Carousel>
      ) : (
        <CardContent className={cn("p-4 text-right", isHebrew ? "text-right" : "text-left")}>
          <Alert variant='info'>
            <div className='absolute top-6'>
              <File className='h-4 w-4' />
            </div>
            <AlertTitle className={isHebrew ? "pr-7" : "pl-7"}>
              {translate("Your pinned tasks will appear here")}!
            </AlertTitle>
            <AlertDescription className={isHebrew ? "pr-7" : "pl-7"}>
              {translate("Go to your projects to pin any tasks")}.
            </AlertDescription>
          </Alert>
        </CardContent>
      )}
    </Card>
  )
}

export default PinnedTasks
